import React from 'react';
import { Button } from '../../atoms/Button';
import { CartButton } from '../../molecules/CartButton';
import { ResumePurchaseBox } from '../../molecules/ResumePurchaseBox';
import { MenuSection } from '../../molecules/MenuSection';
import { MenuItem } from '../../molecules/MenuItem';
import cartIcon from '../../assets/images/btn_shopping_cart_cropped.png';
import basketYellowIcon from '../../assets/images/icon_basket_yellow.png';
import crossButtonIcon from '../../assets/images/cross-button.png';

export interface MenuItemData {
  /**
   * Menu item URL
   */
  href: string;
  /**
   * Menu item label
   */
  label: string;
  /**
   * Whether this item is currently active
   */
  active?: boolean;
  /**
   * Whether this is an external link (opens in new tab)
   */
  external?: boolean;
  /**
   * Link target attribute
   */
  target?: '_blank' | '_self' | '_parent' | '_top';
  /**
   * Link title attribute
   */
  title?: string;
  /**
   * Nested child items (rendered when expanded)
   */
  children?: MenuItemData[];
  /**
   * Whether this item's children should be visible (set when any child is active)
   */
  expanded?: boolean;
}

export interface MenuSectionData {
  /**
   * Section title
   */
  title: string;
  /**
   * Menu items in this section
   */
  items: MenuItemData[];
}

export interface SidebarProps {
  /**
   * Whether to show the cart button
   */
  showCart?: boolean;
  /**
   * Whether to show the resume purchase box
   */
  showResumePurchase?: boolean;
  /**
   * Resume purchase box data
   */
  resumePurchaseData?: {
    resumeUrl: string;
    title?: string;
    message?: string;
    resumeText?: string;
    dismissText?: string;
    onClose?: () => void;
    onDismiss?: () => void;
  };
  /**
   * Menu sections to render
   */
  sections: MenuSectionData[];
  /**
   * Whether the mobile menu is open (only affects mobile <768px)
   */
  isOpen?: boolean;
}

const LOGOUT_URL = '/manage/logout.cgi';

export const Sidebar: React.FC<SidebarProps> = ({
  showCart = false,
  showResumePurchase = false,
  resumePurchaseData,
  sections = [],
  isOpen = false,
}) => {
  const sidebarClasses = `hi-ui hi-ui-sidebar ${isOpen ? 'hi-ui-sidebar-open' : ''}`.trim();

  return (
    <aside className={sidebarClasses} id="hi-ui-sidebar">
      {/* Mobile: Logout button at top */}
      <div className="hi-ui-sidebar-logout-mobile">
        <Button as="a" href={LOGOUT_URL} variant="secondary" size="medium" fullWidth>
          Log Out
        </Button>
      </div>

      {showResumePurchase && resumePurchaseData && (
        <>
          {/* Desktop: Centered resume purchase box */}
          <div className="hi-ui-resume-purchase-desktop">
            <ResumePurchaseBox
              {...resumePurchaseData}
              resumeIcon={basketYellowIcon}
              dismissIcon={crossButtonIcon}
            />
          </div>
          {/* Mobile: Full-width with padding wrapper */}
          <div className="hi-ui-resume-purchase-mobile">
            <ResumePurchaseBox
              {...resumePurchaseData}
              resumeIcon={basketYellowIcon}
              dismissIcon={crossButtonIcon}
            />
          </div>
        </>
      )}

      {showCart && (
        <>
          {/* Desktop: Image-based cart button */}
          <div className="hi-ui-cart-desktop">
            <CartButton href="/manage/basket.cgi" icon={cartIcon} />
          </div>
          {/* Mobile: Button component */}
          <div className="hi-ui-cart-mobile">
            <Button as="a" href="/manage/basket.cgi" variant="primary" size="medium" fullWidth>
              View Shopping Basket
            </Button>
          </div>
        </>
      )}

      {sections.map((section, sectionIndex) => (
        <MenuSection key={`${section.title}-${sectionIndex}`} title={section.title}>
          {section.items.map((item, itemIndex) => (
            <MenuItem
              key={`${item.href}-${itemIndex}`}
              href={item.href}
              active={item.active}
              external={item.external}
              target={item.target}
              title={item.title}
              subItems={
                item.expanded && item.children ? (
                  <>
                    {item.children.map((child, childIndex) => (
                      <MenuItem
                        key={`${child.href}-${childIndex}`}
                        href={child.href}
                        active={child.active}
                        isSubItem
                        external={child.external}
                        target={child.target}
                        title={child.title}
                      >
                        {child.label}
                      </MenuItem>
                    ))}
                  </>
                ) : undefined
              }
            >
              {item.label}
            </MenuItem>
          ))}
        </MenuSection>
      ))}
    </aside>
  );
};
